<template>
  <div :ref="id" :id="id" class="stacked-bar-chart-ct"></div>
</template>
    
<script>
import * as d3 from "d3";
import d3Tip from "d3-tip";
import { getFunctionGetWFString } from "@/services/data-tools.js";

export default {
  name: "BarStackedChart",
  props: {
    data: Object,
    xAttrName: String,
    id: String
  },
  data() {
    return {
      chart: {
        barsNumber: 10,
        data: [
        ],
        selected: [],
        selectedNames: []
      }
    };
  },
  mounted() {
    this.chart.selected = this.data.selected;
    this.chart.selectedNames = this.data.selectedNames;
    this.chart.data = this.data.values;
    this.prepareChart();
  },
  watch: {
    "data": function() {
      this.chart.selected = this.data.selected;
      this.chart.selectedNames = this.data.selectedNames;
      this.chart.data = this.data.values;
      if(!this.chart.data.length) {
        return;
      }
      this.prepareChart();
      this.buildBars(0, this.chart.barsNumber);
      this.buildSubBars();
    }
  },
  methods: {
    prepareChart: function() {
      var margin = { top: 10, right: 30, bottom: 20, left: 100 },
        width = this.$refs[this.id].clientWidth - margin.left - margin.right,
        height = this.$refs[this.id].clientHeight - margin.top - margin.bottom;
      this.chart.barsNumber = width / 50;
      this.chart.width = width;
      this.chart.height = height;
      this.chart.margin = margin;
      
      // Clean the svg
      d3.select("#svg-" + this.id).remove();
      d3.selectAll('.chart-tooltip').remove();
      
      var tooltip = d3.select("#" + this.id).append("div").attr("class", "toolTip");
      this.chart.tooltip = tooltip;
      var svg = d3
        .select('#' + this.id)
        .append("svg")
        .attr("id", "svg-" +this.id)
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

      this.chart.svg = svg;

      // List of subgroups = header of the csv files = soil condition here
      var subgroups = ["greenWF", "blueWF", "greyWF"];
      this.chart.subgroups = subgroups;

      // Add Y axis
      var yScale = d3
        .scaleLinear()
        .domain([0, d3.max(this.chart.data.map(function(d){ return d.wf}))])
        .range([height - 100, 0]);
      svg.append("g").call(d3.axisLeft(yScale)
        .tickFormat(() => ''))
        .selectAll('g')
        .append("svg:foreignObject")
            .attr("width",100)
            .attr("height",20)
            .attr("x", -110)
            .attr("y", -8)
        .append("xhtml:div")
            .html(d =>  `<div class="chart-axis-label">${getFunctionGetWFString("yr")(d)}</div>`);
     
      
      // .tickFormat(d => `${this.formatTick(d)} m${this.superscript[3]}/`));
      this.chart.yScale = yScale;
      // color palette = one color per subgroup
      var color = d3
        .scaleOrdinal()
        .domain(subgroups)
        .range(["#6db3a2", "#359bd1", "#edebe5"]);
      this.chart.color = color;

      this.buildBars(0, this.chart.barsNumber);
      var displayed = d3
        .scaleQuantize()
        .domain([0, width])
        .range(d3.range(this.chart.data.length));
      this.chart.displayed = displayed;
      this.buildSubBars();
      svg
        .append("rect")
        .attr("id", "rect-mover")
        .attr("transform", "translate(0, " + (height - 50) + ")")
        .attr("class", "mover")
        .attr("x", 0)
        .attr("y", 0)
        .attr("height", 50)
        .attr(
          "width",
          Math.round(
            parseFloat(this.chart.barsNumber * width) / this.chart.data.length
          )
        )
        .attr("pointer-events", "all")
        .attr("cursor", "ew-resize")
        .call(d3.drag().on("drag", this.display));
    },
    display: function() {
      var x = parseInt(d3.select("#rect-mover").attr("x")),
        nx = x + d3.event.dx,
        w = parseInt(d3.select("#rect-mover").attr("width")),
        f,
        nf;
      if (nx < 0 || nx + w > this.chart.width) return;

      d3.select("#rect-mover").attr("x", nx);

      f = this.chart.displayed(x);
      nf = this.chart.displayed(nx);

      if (f === nf) return;
      d3.selectAll('.chart-tooltip').remove();
      this.buildBars(nf, this.chart.barsNumber);
    },
    buildSubBars: function() {
      var width = this.chart.width;
      var height = this.chart.height;
      var xAttribute = this.xAttrName;
      var yScale = this.chart.yScale;
      var xOverview = d3
        .scaleBand()
        .domain(
          this.chart.data.map(function(d) {
            return d[xAttribute];
          })
        )
        .range([0, width])
        .padding([0.2]);

      var yOverview = d3.scaleLinear().range([50, 0]);
      yOverview.domain(yScale.domain());

      this.chart.svg.selectAll(".subBar").remove();
      var subBars = this.chart.svg.selectAll(".subBar").data(this.chart.data);

      subBars
        .enter()
        .append("rect")
        .classed("subBar", true)
        .attr("x", function(d) {
          return xOverview(d[xAttribute]);
        })
        .attr("y", function(d) {
          return height - 50 + yOverview(d.wf);
        })
        .attr("width", function() {
          return xOverview.bandwidth();
        })
        .attr("height", function(d) {
          return 50 - yOverview(d.wf);
        });
    },
    buildBars: function(nf, offset) {
      var xAttribute = this.xAttrName;
      var width = this.chart.width;
      var height = this.chart.height;

      var svg = this.chart.svg;
      var subgroups = this.chart.subgroups;
      var color = this.chart.color;
      var yScale = this.chart.yScale;
      var data = this.chart.data.slice(nf, nf + offset);
      
      var tooltip = d3Tip()
          .attr('class', 'chart-tooltip')
          .offset([-10, 0])
          .html(d => 
              `<p><strong>${d.data[xAttribute]}</strong></p>
              <p>Total water footprint: ${getFunctionGetWFString("yr")(d.data.wf)}</p>
              <p>Grey: ${getFunctionGetWFString("yr")(d.data.greyWF)} (${(d.data.greyWF / d.data.wf * 100).toFixed(2)}%)<br/>
              Blue: ${getFunctionGetWFString("yr")(d.data.blueWF)} (${(d.data.blueWF / d.data.wf * 100).toFixed(2)}%)<br/>
              Green: ${getFunctionGetWFString("yr")(d.data.greenWF)} (${(d.data.greenWF / d.data.wf * 100).toFixed(2)}%)</p>`
          );
      svg.call(tooltip);

      d3.select("#labels").remove();
      d3.selectAll(".bars").remove();
      var groups = d3
        .map(data, function(d) {
          return d[xAttribute]
        })
        .keys();

      var xScale = d3
        .scaleBand()
        .domain(groups)
        .range([0, width])
        .padding([0.2]);

      svg
        .append("g")
        .attr("id", "labels")
        .attr("transform", "translate(0,"+ (height - 100) + ")")      // This controls the rotate position of the Axis
        .call(d3.axisBottom(xScale))
        .selectAll("text")
          .attr("transform", function(d, i){
            var tmpY = i % 2 === 0 ? 10 : 25;
            return "translate(0," + tmpY + ")"; })
          .style("font-size", 12)
          .style("font-weight", (d) => {
            return this.chart.selectedNames.includes(d) ? 700 : 200;
          });



      var stackedData = d3.stack().keys(subgroups)(data);
      svg
        .append("g")
        .selectAll("g")
        // Enter in the stack data = loop key per key = group per group
        .data(stackedData)
        .enter()
        .append("g")
        .attr("fill", function(d) {
          return color(d.key);
        })
        .selectAll("rect")
        // enter a second time = loop subgroup per subgroup to add all rectangles
        .data(function(d) {
          return d;
        })
        .enter()
        .append("rect")
        .attr("class", "bars")
        .attr("x", function(d) {
          return xScale(d.data[xAttribute]);
        })
        .attr("y", function(d) {
          return yScale(d[1]);
        })
        .attr("height", function(d) {
          return yScale(d[0]) - yScale(d[1]);
        })
        .attr("width", xScale.bandwidth())
        .attr("stroke", "grey")
        .on('mouseover', tooltip.show)
        .on('mouseout', tooltip.hide)
    },
  }
};
</script>

<style lang="scss">
.stacked-bar-chart-ct {
  height: 80vh;
  margin: auto;
}

.subBar {
  fill: #a6a4a0;
  opacity: 0.5;
}

.mover {
  stroke: red;
  stroke-opacity: 0.1;
  fill: #edebe5;
  fill-opacity: 0.5;
}
</style>
